import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

// Breakpoints per claude.md: 992px tablet/mobile, 320px minimum
const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const GreenValleyCaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="Green Valley Elementary: From 69% to 89% STAAR RLA Passing | Curipod Case Study"
      subTitle="See how Curipod drives measurable student growth — from 69% to 89% STAAR RLA passing rates at Green Valley Elementary, Schertz-Cibolo-Universal City ISD."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>CASE STUDY</HeroTag>
            <HeroTitle>From 69% to 89%</HeroTitle>
            <HeroSubtitle>Green Valley&apos;s Third-Grade Growth Story</HeroSubtitle>
            <HeroDescription>
              How a team of dedicated teachers, an instructional coach, and Curipod
              transformed STAAR RLA results and student confidence at Green Valley
              Elementary — Schertz-Cibolo-Universal City ISD, Region 20, northeast of San
              Antonio.
            </HeroDescription>
            <HeroMeta>
              <MetaBadge>Schertz-Cibolo-Universal City ISD</MetaBadge>
              <MetaBadge>3rd Grade RLA</MetaBadge>
              <MetaBadge>2024–2025 School Year</MetaBadge>
            </HeroMeta>
            <HeroCtaWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <HeroCtaButton type="primary">Sign up for free</HeroCtaButton>
                </a>
              </Link>
            </HeroCtaWrapper>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    69<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>STAAR RLA Passing Rate</StatLabel>
                  <StatYear>2023–2024 · No Curipod</StatYear>
                </StatItem>
                <ArrowContainer>→</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    89<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>STAAR RLA Passing Rate</StatLabel>
                  <StatYear>2024–2025 · With Curipod</StatYear>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem>
                  <StatNumber>
                    39<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Students Scoring Above Zero</StatLabel>
                  <StatYear>2023–2024 · No Curipod</StatYear>
                </StatItem>
                <ArrowContainer>→</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    61<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Students Scoring Above Zero</StatLabel>
                  <StatYear>2024–2025 · With Curipod</StatYear>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* THE CHALLENGE */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid>
            <div>
              <SectionTag>FROM PRESSURE TO PROGRESS</SectionTag>
              <SectionTitle>A Familiar Challenge</SectionTitle>
              <SectionText>
                At Green Valley Elementary in Schertz-Cibolo-Universal City Independent
                School District, northeast of San Antonio in Region 20, third-grade
                teachers <strong>Kimberly McDaniel</strong> and{' '}
                <strong>Luanne Retzloff</strong> faced a familiar challenge: lagging STAAR
                scores and high student anxiety.
              </SectionText>
              <SectionText>
                After more than a decade teaching together in third grade, Kimberly and
                Luanne knew the toll of testing could take. They wanted a way to help
                students build skills and confidence, without the stress that testing
                season can bring.
              </SectionText>
              <BlockQuote>
                <QuoteMark>&ldquo;</QuoteMark>
                <QuoteText>
                  We trusted the process. After that first lesson, we were all in.
                </QuoteText>
                <QuoteAttrib>— Luanne Retzloff, 3rd Grade Teacher</QuoteAttrib>
              </BlockQuote>
            </div>

            <TeamCard>
              <TeamPhotoArea>
                <CircularImageWrap $size={150}>
                  <Image
                    src="/case-study-green-valley/teachers-team.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Green Valley teaching team"
                  />
                </CircularImageWrap>
                <TeamPhotoCaption>The Green Valley Teaching Team</TeamPhotoCaption>
              </TeamPhotoArea>
              <TeamCardTitle>The Green Valley Team</TeamCardTitle>
              <TeamMemberList>
                <TeamMember>
                  <TeamRole>3rd Grade Teachers</TeamRole>
                  <TeamName>Kimberly McDaniel &amp; Luanne Retzloff</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Instructional Coach</TeamRole>
                  <TeamName>Carissa Livingston</TeamName>
                  <TeamNote>&ldquo;Do whatever it takes&rdquo; approach</TeamNote>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Principal</TeamRole>
                  <TeamName>Trina Simmons</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Superintendent</TeamRole>
                  <TeamName>Paige Meloni</TeamName>
                </TeamMember>
              </TeamMemberList>
            </TeamCard>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* FROM CHALLENGE TO CHANGE */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              FROM CHALLENGE TO CHANGE
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              How They Made It Happen
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Instructional Coach Carissa Livingston modeled a Curipod lesson in their
              classrooms. What began as curiosity turned into conviction. With the support
              of their visionary principal, Trina Simmons, the team began using Curipod
              twice a month, then ramped up to weekly sessions ahead of STAAR.
            </SectionText>

            <FocusAreaGrid>
              {['Feedback', 'Writing Growth', 'Engagement', 'Confidence'].map(area => (
                <FocusAreaBadge key={area}>{area}</FocusAreaBadge>
              ))}
            </FocusAreaGrid>

            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '40px auto 0' }}
            >
              By spring, Curipod had become a staple in both RLA and social studies,
              helping turn dry textbook lessons into interactive, formative learning
              experiences.
            </SectionText>

            <BlockQuote style={{ maxWidth: 620, margin: '40px auto 0' }}>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                It gave our kids something new to look forward to. Every day we did
                Curipod, it was a different challenge.
              </QuoteText>
              <QuoteAttrib>— Kimberly McDaniel, 3rd Grade Teacher</QuoteAttrib>
            </BlockQuote>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* THE CURIPOD SHIFT — 5 IMPACT AREAS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <SectionTag style={{ display: 'block', textAlign: 'center' }}>
            THE CURIPOD SHIFT
          </SectionTag>
          <SectionTitle style={{ textAlign: 'center' }}>
            Five Ways Curipod Changed the Classroom
          </SectionTitle>
          <ImpactGrid>
            <ImpactCard>
              <ImpactIcon>💬</ImpactIcon>
              <ImpactTitle>Immediate, Actionable Feedback</ImpactTitle>
              <ImpactQuote>
                &ldquo;It&apos;s the same advice I&apos;ve told them a million times, but
                when they read it in feedback, they act on it. Literally in five minutes,
                their writing goes from this to this.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Kimberly McDaniel</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>🎯</ImpactIcon>
              <ImpactTitle>Engagement and Joyful Challenge</ImpactTitle>
              <ImpactQuote>
                &ldquo;That variety and immediacy transformed writing practice from
                something students dreaded into something they wanted to do.&rdquo;
              </ImpactQuote>
              <ImpactSubQuote>
                &ldquo;They love the podium and the voting. It motivates them to
                improve.&rdquo;
              </ImpactSubQuote>
              <ImpactAttrib>— Kimberly McDaniel</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>⭐</ImpactIcon>
              <ImpactTitle>Confidence and Inclusion</ImpactTitle>
              <ImpactQuote>
                &ldquo;A super quiet boy is one of the top-voted answers every time. He
                doesn&apos;t want to speak out loud, but he&apos;s proud when people read
                his writing.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Luanne Retzloff</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>📏</ImpactIcon>
              <ImpactTitle>Calibration and Coaching</ImpactTitle>
              <ImpactQuote>
                &ldquo;We needed consistency at STAAR rigor. Curipod removed bias: same
                prompt, same rubric, same standard for every student. Teachers even
                learned by writing and scoring themselves first.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Carissa Livingston, Instructional Coach</ImpactAttrib>
            </ImpactCard>

            <ImpactCard $wide>
              <ImpactIcon>🤝</ImpactIcon>
              <ImpactTitle>Collaborative Culture</ImpactTitle>
              <ImpactQuote>
                &ldquo;They encourage each other. &lsquo;Let&apos;s look at your feedback.
                What can you do?&rsquo; That community piece has been huge.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Luanne Retzloff</ImpactAttrib>
            </ImpactCard>
          </ImpactGrid>
        </MaxWidthContainer>
      </Section>

      {/* RESULTS THAT SPEAK FOR THEMSELVES */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              RESULTS THAT SPEAK FOR THEMSELVES
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              Highest Scores in the District
            </SectionTitle>
            <SectionText
              style={{
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                maxWidth: 720,
                margin: '0 auto 40px',
              }}
            >
              Green Valley&apos;s third graders achieved the highest scores in the
              district, catching the attention of Superintendent Paige Meloni.
            </SectionText>

            <ResultBlockQuote>
              <QuoteMark style={{ color: theme.colors.orange }}>&ldquo;</QuoteMark>
              <QuoteText style={{ color: theme.colors.white }}>
                She came to our PLC and asked what we were doing. We said, Curipod.
              </QuoteText>
              <QuoteAttrib style={{ color: theme.colors.orange }}>
                — Kimberly McDaniel, 3rd Grade Teacher
              </QuoteAttrib>
            </ResultBlockQuote>

            <ResultNote>
              Along with their impressive growth in Meets and Masters, teachers noted a
              major rise in students scoring above zero — from{' '}
              <strong>39% up to 61%</strong>. That shift reflects more than better
              writing; it shows growing confidence and a willingness to try.
            </ResultNote>

            <ResultsStudentsPhoto>
              <CircularImageWrap $size={160} $borderColor="rgba(249,151,70,0.6)">
                <Image
                  src="/case-study-green-valley/students-classroom.png"
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center top"
                  alt="Green Valley third-grade students"
                />
              </CircularImageWrap>
              <ResultsPhotoLabel>Green Valley third-grade students</ResultsPhotoLabel>
            </ResultsStudentsPhoto>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* WHY IT MATTERS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              WHY IT MATTERS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>Why It Matters</SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              For Green Valley&apos;s third-grade team, Curipod didn&apos;t just boost
              scores. It shifted the mindset. Their results prove what happens when strong
              teaching meets the right tool. And it all began with trust — trusting the
              process, trusting one another, and helping students learn to trust
              themselves.
            </SectionText>

            <PeoplePhotoRow>
              <PhotoItem>
                <CircularImageWrap $size={110}>
                  <Image
                    src="/case-study-green-valley/teachers-team.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Teaching team"
                  />
                </CircularImageWrap>
                <PhotoLabel>Teaching team</PhotoLabel>
              </PhotoItem>
              <PhotoItem>
                <CircularImageWrap $size={110}>
                  <Image
                    src="/case-study-green-valley/students-classroom.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center top"
                    alt="Third-grade students"
                  />
                </CircularImageWrap>
                <PhotoLabel>Third-grade students</PhotoLabel>
              </PhotoItem>
              <PhotoItem>
                <CircularImageWrap $size={110}>
                  <Image
                    src="/case-study-green-valley/student-portrait.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center top"
                    alt="Student spotlight"
                  />
                </CircularImageWrap>
                <PhotoLabel>Student spotlight</PhotoLabel>
              </PhotoItem>
            </PeoplePhotoRow>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                It&apos;s not just about numbers. Our kids believe they can do hard things
                now.
              </FinalQuoteText>
              <QuoteAttrib>— Kimberly McDaniel, 3rd Grade Teacher</QuoteAttrib>
            </FinalQuoteCard>

            <TagRow>
              {['Feedback', 'Confidence', 'Engagement', 'Equity'].map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagRow>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIcon aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIcon>
            <DownloadText>
              <DownloadTitle>Download the Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the Green Valley case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="/Curipod_Case_Study_Green_Valley.pdf"
              download="Curipod_Case_Study_Green_Valley.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default GreenValleyCaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const HeroCtaWrapper = styled.div`
  margin-top: 36px;
`;

const HeroCtaButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 48px;
  font-size: 20px;
  height: 58px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.35);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }

  @media ${MOBILE} {
    font-size: 18px;
    height: 52px;
    padding: 0 36px;
  }
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 20px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: nowrap;

  @media ${MOBILE} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 28px;
  justify-self: center;
  flex-shrink: 0;

  @media ${MOBILE} {
    width: 60px;
    height: 1px;
    margin: 0 auto;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.4)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${theme.colors.white};
  line-height: 1;
  display: flex;
  align-items: flex-start;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatPercent = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const StatYear = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 2px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: ${theme.colors.white};
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── CIRCULAR IMAGE ─────────────────────────────────────────────────────────── */

const CircularImageWrap = styled.div<{ $size: number; $borderColor?: string }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid ${({ $borderColor }) => $borderColor ?? theme.colors.orange};
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

/* ─── CHALLENGE SECTION ──────────────────────────────────────────────────────── */

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

const TeamCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const TeamPhotoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 2px solid ${theme.colors.beigeDark};
`;

const TeamPhotoCaption = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  margin-top: 10px;
  font-style: italic;
  text-align: center;
`;

const TeamCardTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const TeamMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamMember = styled.div``;

const TeamRole = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const TeamName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

const TeamNote = styled.div`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  margin-top: 2px;
`;

/* ─── FOCUS AREAS ────────────────────────────────────────────────────────────── */

const FocusAreaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const FocusAreaBadge = styled.div`
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: bold;
  padding: 10px 24px;
  border-radius: 30px;
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div<{ $wide?: boolean }>`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactQuote = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactSubQuote = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactAttrib = styled.cite`
  font-size: 13px;
  font-weight: bold;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultBlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 0 0 48px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 10px 10px 0;
  max-width: 640px;
  width: 100%;
`;

const ResultNote = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  max-width: 680px;
  line-height: 1.75;
  text-align: center;
  margin: 0 0 48px;

  strong {
    color: ${theme.colors.orange};
    font-weight: 700;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const ResultsStudentsPhoto = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ResultsPhotoLabel = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  text-align: center;
  margin: 0;
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const PeoplePhotoRow = styled.div`
  display: flex;
  gap: 32px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 48px;

  @media ${MOBILE} {
    gap: 20px;
    margin-bottom: 32px;
  }
`;

const PhotoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const PhotoLabel = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  text-align: center;
  margin: 0;
`;

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.4;
  margin: 0 0 16px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Tag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIcon = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
